import {
  type IntegrationsMarketplaceEHRResponse,
  type IntegrationsMarketplaceEHRSimpleResponse,
  type IntegrationsMarketplaceListResponse,
} from '@/types/global-types';
import { globalHttp } from '@/utils/globalHTTP';
import { type EHRProvider } from '@heidi/schema/ml-scribe';

// Carved to `@heidi/templates/data` at ARCFE-1046 (D2.7) with its two consumers,
// the community and form gallery lists. NAMED re-exports, not a star. The whole
// `URLSearchParams` assembly moved verbatim: the repeated `filter` appends and
// the string `enable_recommendation` flag are wire behaviour, not style.
export {
  communityTemplatePageSize,
  getPublicTemplates,
  type GetPublicTemplatesParams,
} from '@heidi/templates/data';

// Carved to `@heidi/templates/data` at ARCFE-1026 — the FIRST consumer of the
// `configureTemplatesData` community seam. It runs on `globalHttp`
// (unauthenticated: the public gallery is readable without a session), which is
// exactly why it crosses the seam instead of the scribe registry.
export { getTemplateDetails } from '@heidi/templates/data';

export type GetIntegrationMarketplaceIntegrationsParams = {
  language?: string;
  include_english?: boolean;
};

// Integration Marketplace
export const getIntegrationMarketplaceIntegrations = (
  params: GetIntegrationMarketplaceIntegrationsParams
) => {
  const searchParams = new URLSearchParams();
  if (params.language) {
    searchParams.append('language', params.language);
  }
  if (params.include_english) {
    searchParams.append('include_english', params.include_english.toString());
  }

  return globalHttp.get<IntegrationsMarketplaceListResponse>(
    `/template-gallery/integrations-marketplace/integrations`,
    { params: searchParams }
  );
};

export const getIntegrationMarketplaceIntegrationBySlug = (
  language: string,
  integrationSlug: string
) => {
  return globalHttp.get<IntegrationsMarketplaceEHRResponse>(
    `/template-gallery/integrations-marketplace/integrations/${language}/${integrationSlug}`
  );
};

export const getIntegrationMarketplaceDetails = ({
  ehr_provider,
  language,
  use_simple_response,
}: {
  ehr_provider: EHRProvider;
  language?: string;
  use_simple_response?: boolean;
}) => {
  return globalHttp.get<IntegrationsMarketplaceEHRSimpleResponse>(
    `/template-gallery/integrations-marketplace/integrations/general`,
    {
      params: {
        ehr_provider,
        use_simple_response,
        language: language || 'en',
        include_unpublished: 'true',
      },
    }
  );
};

// ==================== Community Template Likes ====================

// The template-LIKE surface moved to `@heidi/templates/data` (S2, ARCFE-1017).
// It split cleanly from the rest of this module: the gallery reads run on
// `globalHttp` (unauthenticated, still here), while likes run on
// `scribeHttpV2` — so the likes go to the package over the `@heidi/api/client`
// REGISTRY and never touch the `configureTemplatesData` community seam. The
// S2 cohort splits by FUNCTION, not by module.
export {
  getBatchLikeStatus,
  getLikeStatus,
  likeTemplate,
  unlikeTemplate,
} from '@heidi/templates/data';
// The response shapes are contracts, not data — they come from the inert door.
export type {
  BatchLikeStatusResponse,
  LikeResponse,
  LikeStatusResponse,
} from '@heidi/templates/contracts';
